import React, { useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';

interface RateLimitToastProps {
  message: string;
  remainingTime: number;
  onClose: () => void;
}

const ToastContainer = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 80px;
  right: 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--gap-md) var(--gap-lg);
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  min-width: 300px;
  transform: translateX(${props => props.visible ? '0' : '100%'});
  opacity: ${props => props.visible ? '1' : '0'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    right: 10px;
    left: 10px;
    min-width: auto;
    width: calc(100% - 20px);
  }
`;

const ToastHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--gap-sm);
`;

const ToastTitle = styled.div`
  color: var(--color-text);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  display: flex;
  align-items: center;
  gap: var(--gap-sm);
`;

const WarningIcon = styled.span`
  color: #f59e0b;
  font-size: var(--font-size-lg);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 2px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 16px;
  
  &:hover {
    color: var(--color-text);
    background: var(--color-surface-hover);
  }
`;

const ToastMessage = styled.div`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.4;
  margin-bottom: var(--gap-sm);
`;

const CountdownBar = styled.div`
  width: 100%;
  height: 4px;
  background: var(--color-surface-hover);
  border-radius: 2px;
  overflow: hidden;
`;

const CountdownProgress = styled.div<{ progress: number }>`
  height: 100%;
  background: #f59e0b;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
  border-radius: 2px;
`;

const CountdownText = styled.div`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-align: center;
  margin-top: var(--gap-xs);
`;

export const RateLimitToast: React.FC<RateLimitToastProps> = ({ 
  message, 
  remainingTime: initialTime, 
  onClose 
}) => {
  const [visible, setVisible] = useState(false);
  const [remainingTime, setRemainingTime] = useState(initialTime);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  }, [onClose]);

  useEffect(() => {
    // Show toast after a brief delay for animation
    const showTimer = setTimeout(() => setVisible(true), 100);
    
    // Update countdown every second
    const countdownTimer = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          // Auto-close when countdown reaches 0
          handleClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(showTimer);
      clearInterval(countdownTimer);
    };
  }, [handleClose]);

  const progress = initialTime > 0 ? ((initialTime - remainingTime) / initialTime) * 100 : 100;

  return (
    <ToastContainer visible={visible}>
      <ToastHeader>
        <ToastTitle>
          <WarningIcon>⚠️</WarningIcon>
          Rate Limited
        </ToastTitle>
        <CloseButton onClick={handleClose} title="Close">
          ×
        </CloseButton>
      </ToastHeader>
      
      <ToastMessage>{message}</ToastMessage>
      
      {remainingTime > 0 && (
        <>
          <CountdownBar>
            <CountdownProgress progress={progress} />
          </CountdownBar>
          <CountdownText>
            {remainingTime} second{remainingTime !== 1 ? 's' : ''} remaining
          </CountdownText>
        </>
      )}
    </ToastContainer>
  );
};